import { MasterDataCrud } from '@diamond/shared/schema/master-data';
import { CG3BusinessTypes } from '@diamond/shared/types';
import { apiInstance } from '@diamond/shared/utils';
import { useAuthStore } from '@diamond/sol-admin/features/auth';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

const BusinessTypeQueryKey = 'BusinessType';

export type BusinessTypesQuery = MasterDataCrud['getBusinessTypes']['query'];

export function businessTypesQueryKey(query: BusinessTypesQuery = {}) {
  return [BusinessTypeQueryKey, query];
}

export const getBusinessTypes = async (
  token: string,
  query: BusinessTypesQuery = {}
) => {
  const hasQuery = Object.keys(query).length > 0;
  const { data } = await apiInstance(token).get<CG3BusinessTypes>(
    `/backoffice/master-data/business-types`,
    hasQuery ? { params: query } : undefined
  );
  return data;
};

function getBusinessTypeQueryOptions(
  token: string,
  query: BusinessTypesQuery = {}
) {
  return queryOptions({
    queryKey: businessTypesQueryKey(query),
    queryFn: () => getBusinessTypes(token, query),
    select(data) {
      return data.data.filter((it) => it.business_type_desc !== '');
    },
    staleTime: 60_000,
    retry: false,
  });
}

export const useBusinessTypes = (hookOptions?: {
  enabled?: boolean;
  query?: BusinessTypesQuery;
}) => {
  const token = useAuthStore((s) => s.access_token);
  const query = hookOptions?.query ?? {};

  const base = getBusinessTypeQueryOptions(token, query);
  const reactQuery = useQuery({
    ...base,
    enabled: hookOptions?.enabled ?? true,
  });

  const selectOptions = useMemo(() => {
    return (reactQuery.data || []).map((it) => ({
      label: it.business_type_desc,
      value: it.business_type_id,
    }));
  }, [reactQuery.data]);

  const maps = useMemo(() => {
    return selectOptions.reduce((p: Record<string, string>, c) => {
      p[c.value] = c.label;
      return p;
    }, {});
  }, [selectOptions]);

  return {
    ...reactQuery,
    options: selectOptions,
    maps,
  };
};
